import React from "react";
import PropTypes from "prop-types";
import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import tinycolor from "tinycolor2";
import {
  ButtonHOC,
  DefaultBtn,
  DefaultMSFNavBtn,
  DefaultSearchBtn,
} from "../HOCs";
import jssVariables from "../../../../jss_setup";
import {
  commonStylesBasic,
  commonStylesIconBasic,
  commonStylesPlaceholderBasic,
  getCommonStylesLabelBasic,
} from "../helpers";

const commonStyles = {
  ...commonStylesBasic,
  borderRadius: 0,
  padding: `calc(${jssVariables.normal_gap} / 4) calc(${jssVariables.normal_gap} * 1.5)`,
  background: jssVariables.colors.background.dark,
  border: `2px solid ${jssVariables.colors.border}`,
  "&:hover": {
    color: jssVariables.colors.text.dark,
    background: "white",
  },
};

const icon = {
  ...commonStylesIconBasic,
};

function coloredBtnStyles(props) {
  return props.coloredBtn
    ? {
        ButtonDefault: {
          background: jssVariables.colors.base_color,
          border: props.empty
            ? "2px solid white"
            : `2px solid ${jssVariables.colors.base_color}`,
          "&:hover": {
            color: jssVariables.colors.base_color,
            background: "white",
          },
        },
        ButtonReverted: {
          color: jssVariables.colors.base_color,
          border: !props.empty
            ? `2px solid ${jssVariables.colors.text.dark}`
            : `3px solid ${jssVariables.colors.base_color}`,
          "&:hover": {
            color: !props.empty ? jssVariables.colors.base_color : "white",
            background: !props.empty ? "white" : jssVariables.colors.base_color,
          },
        },
      }
    : {};
}

const ButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderBasic,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  ButtonDefault: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: jssVariables.colors.background.dark,
    color: "white",
    width: "100%",
    border: `2px solid ${jssVariables.colors.background.dark}`,
    ...coloredBtnStyles(props).ButtonDefault,
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  ButtonReverted: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: "white",
    color: jssVariables.colors.background.dark,
    border: "2px solid white",
    "&:hover": {
      color: "white",
      background: jssVariables.colors.background.dark,
    },
    ...coloredBtnStyles(props).ButtonReverted,
    ..._.get(props.customStyles, "ButtonReverted", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...icon,
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    ButtonDefault: (props) => ({
      ..._.get(props.customStyles, "ButtonDefault.smallscreen", {}),
    }),
    ButtonReverted: (props) => ({
      ..._.get(props.customStyles, "ButtonReverted.smallscreen", {}),
    }),
  },
});
var Button = ({ text, showIcon, ...props }) => {
  return (
    <DefaultBtn {...props}>
      <span className={props.classes.label}>{text}</span>
      {showIcon && (
        <InlineIcon
          icon={props.icon ? props.icon : "fa:angle-right"}
          className={props.classes.icon}
        />
      )}
    </DefaultBtn>
  );
};
Button.defaultProps = {
  text: "",
  showIcon: true,
  classes: {},
};
Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  showIcon: PropTypes.bool,
};
Button = ButtonHOC(Button, ButtonStyles);

//---------------------------------------
//-------- SearchButton -----------------
const SearchButtonStyles = createUseStyles({
  placeholder: (props) => ({
    height: "3rem !important",
    minWidth: "10rem",
    borderRadius: 0,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  SearchButton: (props) => ({
    ...commonStyles,
    background: tinycolor("#262F35").setAlpha(0.7).toRgbString(),
    border: `3px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    height: "3rem",
    minWidth: "10rem",
    fontSize: "large",
    borderRadius: 0,
    "&:hover": {
      background: jssVariables.colors.base_color,
      color: tinycolor("#262F35").setAlpha(0.7).toRgbString(),
    },
    ..._.get(props.customStyles, "SearchButton", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontWeight: "bold",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...icon,
    fontSize: "x-large",
    marginTop: ".2rem",
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
});
var SearchButton = ({ loading, text, ...props }) => {
  return (
    <DefaultSearchBtn {...props}>
      {loading ? (
        <InlineIcon
          icon="line-md:loading-twotone-loop"
          className={props.classes.icon}
        />
      ) : (
        <span className={props.classes.label}>{text}</span>
      )}
    </DefaultSearchBtn>
  );
};
SearchButton.defaultProps = {
  loading: false,
  text: "",
};
SearchButton.propTypes = {
  loading: PropTypes.bool,
  text: PropTypes.string.isRequired,
};
SearchButton = ButtonHOC(SearchButton, SearchButtonStyles);

const MSFNavBtnStyles = createUseStyles({
  placeholder: (props) => ({
    height: "2.5rem!important",
    minWidth: "4rem!important",
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  button: (props) => ({
    ...jssVariables.fonts,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: "none",
    borderBottom: `2px solid transparent`,
    fontWeight: "normal",
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    "&:hover:enabled": {
      borderBottom: `2px solid ${jssVariables.colors.base_color}`,
    },
    "&:disabled": {
      borderBottom: `2px solid ${jssVariables.colors.base_color}`,
      cursor: "unset",
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    color: jssVariables.colors.text.dark,
    fontSize: "large",
    textTransform: "uppercase",
    fontWeight: "bold",
    ..._.get(props.customStyles, "label", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    button: (props) => ({
      ..._.get(props.customStyles, "button.smallscreen", {}),
    }),
  },
});
var MSFNavBtn = ({ text, showIcon, ...props }) => {
  return (
    <DefaultMSFNavBtn {...props}>
      <span className={props.classes.label}>{text}</span>
    </DefaultMSFNavBtn>
  );
};
MSFNavBtn.defaultProps = {
  text: "",
  classes: {},
};
MSFNavBtn.propTypes = {
  text: PropTypes.string.isRequired,
};
MSFNavBtn = ButtonHOC(MSFNavBtn, MSFNavBtnStyles);

export { Button, SearchButton, MSFNavBtn };
